import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { EVENT_VISITS_MIN_VISIBLE_MS } from '@buzzmap/shared';
import { canRecordEventVisit, recordEventVisit } from '../../lib/eventVisits';

const UUID_RE = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

function documentIsVisible(): boolean {
  try {
    if (document.hidden) return false;
    const state = document.visibilityState as string | undefined;
    return state !== 'hidden' && state !== 'prerender';
  } catch {
    return false;
  }
}

/**
 * Records one anonymous event-page visit after five accumulated visible seconds.
 * Mounted once inside the public router; it observes both event pages and the
 * desktop event modal route, and never renders visible UI.
 */
export function EventVisitRecorder() {
  const location = useLocation();
  const enabled = import.meta.env.VITE_EVENT_VISITS_ENABLED === 'true';
  const match = matchPath('/event/:id', location.pathname);
  const routeId = match?.params.id;
  const eventId = routeId && UUID_RE.test(routeId) ? routeId.toLowerCase() : null;

  useEffect(() => {
    if (!enabled || eventId === null || !canRecordEventVisit()) return;

    let accumulatedMs = 0;
    let visibleSince: number | null = documentIsVisible() ? performance.now() : null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempted = false;
    let hiddenByPagehide = false;

    const clearTimer = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const pause = (): void => {
      if (visibleSince !== null) {
        accumulatedMs += Math.max(0, performance.now() - visibleSince);
        visibleSince = null;
      }
      clearTimer();
    };

    const attemptIfReady = (): void => {
      if (attempted || visibleSince === null || !documentIsVisible() || hiddenByPagehide) return;
      accumulatedMs += Math.max(0, performance.now() - visibleSince);
      visibleSince = performance.now();
      if (accumulatedMs < EVENT_VISITS_MIN_VISIBLE_MS) return;
      attempted = true;
      clearTimer();
      try {
        void recordEventVisit(eventId);
      } catch {
        // The recorder is silent by contract; rendering must stay unaffected.
      }
    };

    const scheduleThreshold = (): void => {
      clearTimer();
      if (attempted || visibleSince === null) return;
      const elapsed = accumulatedMs + Math.max(0, performance.now() - visibleSince);
      const remaining = Math.max(0, EVENT_VISITS_MIN_VISIBLE_MS - elapsed);
      timer = setTimeout(() => {
        timer = null;
        if (!documentIsVisible() || hiddenByPagehide) pause();
        else attemptIfReady();
        if (!attempted && visibleSince !== null) scheduleThreshold();
      }, remaining);
    };

    const resume = (): void => {
      if (attempted || visibleSince !== null || hiddenByPagehide || !documentIsVisible()) return;
      if (!canRecordEventVisit()) return;
      visibleSince = performance.now();
      scheduleThreshold();
    };

    const onVisibilityChange = (): void => {
      if (documentIsVisible() && !hiddenByPagehide) resume();
      else pause();
    };

    const onPageHide = (): void => {
      hiddenByPagehide = true;
      pause();
    };

    const onPageShow = (): void => {
      hiddenByPagehide = false;
      resume();
    };

    if (visibleSince !== null) scheduleThreshold();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      pause();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled, eventId]);

  return null;
}
